// Shared TanStack Query client (P2-25, design §5.4 RELOAD/DATA_CHANGED handlers).
//
// `main.ts` previously let `VueQueryPlugin` construct its own internal
// `QueryClient` (no options passed to `app.use(VueQueryPlugin)`), which meant
// nothing OUTSIDE a component's `setup()` could reach it. The P2-25 command
// poller dispatches `RELOAD`/`DATA_CHANGED` handlers from a plain module (no
// component context), so a module-level singleton is needed. Exporting it
// here — and having `main.ts` pass `{ queryClient }` into the plugin instead
// of letting it self-construct — makes the SAME instance reachable both from
// components (`useQueryClient()`) and from `command-handlers.ts`.
import { QueryClient } from '@tanstack/vue-query';

import { ApiError } from './api';

/** Default retry policy for every query in the app (auth-boot/retry-policy
 * audit): a 4xx response (401/403/404/etc.) is a legitimate, non-transient
 * outcome — the request won't succeed by repeating it verbatim — so it is
 * never retried. Only network errors and 5xx get TanStack's normal bounded
 * retry (capped at 3 attempts, same as the library default). Individual
 * queries can still opt into a stricter policy (e.g. `retry: false`
 * everywhere, such as `meLevelSubjectsOptions`) where being explicit at the
 * call site is clearer than relying on this shared default. */
function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < 3;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
    },
  },
});
